import { timeOut, Debouncer, PolymerElement, html } from 'polymer';

import dom from '@eab-pip/pi-dom';

/**
 * @customElement
 * @polymer
 * @appliesMixin HIP.BehaviorBrowserEvents
 * @appliesMixin HIP.BehaviorA11yKeyboard
 * @appliesMixin HIP.BehaviorContainer
 * @appliesMixin HIP.BehaviorElement
 * @appliesMixin HIP.BehaviorFlyout
 * @appliesMixin HIP.BehaviorInput
 */
class TypeaheadElement extends HIP.BehaviorFlyout(
  HIP.BehaviorBrowserEvents(
    HIP.BehaviorContainer(HIP.BehaviorInput(HIP.BehaviorA11yKeyboard(HIP.BehaviorElement(PolymerElement))))
  )
) {
  static get is() {
    return 'hi-typeahead';
  }

  static get template() {
    return html`
      <style is="custom-style" include="hi-theme"></style>
      <style>
        :host {
          --hi-typeahead--height: calc(var(--hi-size--unit-5) + var(--hi-size--unit-2));
          --hi-typeahead--anchor-height: 100%;
          --hi-typeahead--min-width: 4em;
          --hi-typeahead--container-min-units: 1;
          --hi-typeahead--container-max-units: 5;
          --hi-typeahead--border-color: var(--hi-color--gray-70, var(--hi-color--theme-30));
        }

        :host {
          outline: none;
          position: relative;
          user-select: none;
          text-align: left;
          display: block;
          min-width: var(--hi-typeahead--min-width);
          width: var(--hi-form--field-width, auto);
        }

        #flyoutValue {
          display: flex;
          flex-wrap: nowrap;
          width: 100%;
          justify-content: space-between;
          align-items: center;
          height: var(--hi-typeahead--height);
          padding: 0;
          position: relative;
          background-color: var(--hi-color--gray-10);
          border-bottom: var(--hi-fixed--pixel-1) solid var(--hi-typeahead--border-color);
          box-shadow: inset 0 0 1.5px rgba(0, 0, 0, 0.1); /*TODO: remove custom box shadow */
          transition: background-color var(--hi-animation--speed-fast) var(--hi-animation--accel-fast),
            border-color var(--hi-animation--speed-fast) var(--hi-animation--accel-fast);
        }

        :host([aesthetic='tight']) #flyoutValue {
          border: var(--hi-fixed--pixel-1) solid var(--hi-color--theme-30);
          border-radius: var(--hi-size--shape-radius);
          padding: var(--hi-size--small-xx);
        }

        #anchorToggle {
          margin: var(--hi-size--unit-2);
          align-self: center;
        }

        #flyoutContainer {
          background: var(--hi-color--white);
          border-top-width: 0;
          box-shadow: var(--hi-color--shadow-hover);
          display: none;
          margin: 0;
          min-height: calc(var(--hi-size--unit-4) * var(--hi-typeahead--container-min-units));
          outline: none;
          overflow-x: hidden;
          overflow-y: auto;
          padding-bottom: var(--hi-size--unit-2);
          position: absolute;
          width: 100%;
          z-index: 10000;
        }

        :host([aesthetic='tight']) #flyoutContainer {
          border-width: var(--hi-fixed--pixel-1);
          border-radius: var(--hi-size--shape-radius);
          margin-top: var(--hi-size--small-x);
        }

        :host([cap-height]) #flyoutContainer {
          max-height: calc(var(--hi-size--unit-4) * var(--hi-typeahead--container-max-units));
        }

        :host([opened]) #flyoutContainer {
          display: block;
        }

        #minCharacterHelp {
          padding: var(--hi-size--unit-2);
          color: var(--hi-color--gray-60);
          font-size: var(--hi-font-size--text);
          font-weight: var(--hi-font--weight-normal);
          position: absolute;
          right: var(--hi-size--unit-4);
          pointer-events: none;
          user-select: none;
        }

        #warningMessage {
          background: var(--hi-color--red-30);
          padding: var(--hi-size--unit-2);
          color: var(--hi-color--white);
          font-size: var(--hi-font-size--text);
        }

        #input {
          -webkit-appearance: initial;
          border: none;
          font-size: var(--hi-font-size--medium);
          display: block;
          background: var(--hi-color--transparent);
          color: var(--hi-color--text);
          width: 100%;
          flex-grow: 1;
          outline: none;
          box-shadow: none;
          overflow: inherit;
          margin: 0;
          padding: 0;
          cursor: text;
          height: var(--hi-typeahead--height);
        }

        #input::-webkit-search-cancel-button {
          -webkit-appearance: none;
        }

        #inputClear {
          margin-right: var(--hi-size--unit-1);
        }

        :host(:focus) #flyoutValue,
        :host(:focus-within) #flyoutValue {
          border-bottom: var(--hi-fixed--pixel-2) solid var(--hi-color--theme-30);
          background: var(--hi-color--white);
          outline: var(--hi-outline--width) var(--hi-outline--line) var(--hi-color--outline);
          outline-offset: 0;
        }

        :host([viewonly]),
        :host([readonly]) {
          opacity: 1;
        }

        :host([viewonly]),
        :host([readonly]),
        :host([disabled]) {
          cursor: not-allowed !important;
          pointer-events: all !important;
        }

        :host([viewonly]) #flyoutValue,
        :host([readonly]) #flyoutValue,
        :host([disabled]) #flyoutValue,
        :host([viewonly]) #anchorToggle,
        :host([readonly]) #anchorToggle,
        :host([disabled]) #anchorToggle {
          pointer-events: none !important;
          -moz-user-focus: ignore !important;
          user-select: none !important;
          border-color: var(--hi-color--transparent);
          --hi-color--theme-30: var(--hi-color--disabled);
        }
      </style>

      <div id="flyoutValue">
        <hi-icon id="anchorToggle" kind="search" theme="[[ theme ]]" size="large"> </hi-icon>
        <input
          id="input"
          mixin="input-placeholder"
          type="search"
          value="[[ searchValue ]]"
          autofocus$="[[ autofocus ]]"
          disabled$="[[ disabled ]]"
          placeholder$="[[ placeholder ]]"
          maxlength$="[[ max ]]"
          minlength="0"
          name$="[[ name ]]"
          readonly$="[[ readonly ]]"
          required$="[[ required ]]"
          on-invalid="__onInvalid_input"
          on-input="__onInput_input"
        />
        <hi-close
          id="inputClear"
          aria-label$="[[ labelClose ]]"
          on-trigger-ended="__onClose_triggerEnded"
          hidden
          readonly$="[[ readonly ]]"
          disabled$="[[ disabled ]]"
        >
        </hi-close>
        <p id="minCharacterHelp" aria-hidden="true"></p>
      </div>
      <div id="flyoutContainer">
        <p id="warningMessage"></p>
        <slot name="typeaheadHeader"></slot>
        <div id="flyoutContent" aria-haspopup="listbox">
          <slot id="items"></slot>
        </div>
        <slot name="typeaheadFooter"></slot>
      </div>
    `;
  }

  static get properties() {
    return {
      /**
       * @enum
       * - (undefined), update value for every key pressed
       * - edit, update on ENTER or focus out
       */
      kind: {
        type: String
      },
      /**
       * @enum
       * - open, a blank underline
       * - tight, a full border for closer forms
       */
      aesthetic: {
        type: String,
        value: 'open'
      },
      /**
       * controls the typeahead filter, in search mode we expect an external source to provide filtering
       * @enum
       * - filter (default - filters items by `searchValue`)
       * - search (does not filter - for the case that the list is already filtered)
       */
      mode: {
        type: String,
        value: 'filter'
      },
      placeholder: String,
      autofocus: Boolean,
      name: String,
      max: {
        type: Number,
        value: 250
      },
      min: {
        type: Number,
        value: 2
      },
      labelMin: String,
      /**
       * the last value chosen from the typeahead
       */
      value: {
        type: String,
        observer: '__valueChanged',
        reflectToAttribute: true,
        notify: true
      },
      /**
       * set an intial display value
       */
      initialValue: {
        type: String,
        reflectToAttribute: true,
        notify: true
      },
      /**
       * constrain the height of the dropdown to the height of `--hi-typeahead--container-max-units` by a large element size
       */
      capHeight: Boolean,
      /**
       * keep the value in the typeahead after selected
       */
      preserveValue: Boolean,
      /**
       * aria-label for the close button
       * this should be contextual and not generic
       */
      labelClose: {
        type: String,
        value: 'Clear search input'
      },
      /**
       * binding to internal input to disconnect value from interactions
       */
      searchValue: {
        type: String,
        observer: '__searchValueChanged',
        reflectToAttribute: true,
        notify: true
      },
      /**
       * the time (ms) between changing the search and the search list
       * by default there is no delay, assign a positive value to delay the update
       */
      searchDelay: {
        type: Number,
        value: 0
      }
    };
  }

  static get keyBindings() {
    return {
      'enter:keydown': '_onKeyAny__preventDefault',
      'up:keydown': '_onKeyAny__preventDefault',
      'down:keydown': '_onKeyAny__preventDefault',
      'enter:keyup': '__onKeyUp_enter',
      'up:keyup': '__onKeyUp_up',
      'down:keyup': '__onKeyUp_down',
      'esc:keyup': '__onKeyUp_escape'
    };
  }

  /**
   * @deprecated tap-ended
   * use click-ended
   * @event click-ended
   * @event focusin-ended
   */

  static get browserBindings() {
    return {
      click: '_flyout__onTap',
      'focusin:input': '__onFocusIn__input'
    };
  }

  static get autoBinding() {
    return {
      signal: 'opened-changed',
      field: 'opened'
    };
  }

  static get observers() {
    return ['__warningMessageWatcher(labelMin, min, searchValue)'];
  }

  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('aria-autocomplete', 'list');
    this.setAttribute('aria-haspopup', 'listbox');

    // a typeahead configures the dropdown
    this._kindFlyout = 'value';
    this.manual = true;

    if (this.initialValue) {
      this.value = this.searchValue = this.initialValue;
      this.opened = false;
    }

    this.addEventListener('focusout', this._flyout__onFocusOut, true);
  }

  focus() {
    this.$.input.focus();
  }

  blur() {
    this.$.input.blur();
  }

  __onSlotChanged() {
    this._flyout__onSlotChanged();

    const firstSlotChild = this.firstSlotChild;
    if (firstSlotChild) {
      dom.ensureAttr(this.firstSlotChild, 'id');
      this.setAttribute('aria-controls', this.firstSlotChild.getAttribute('id'));
    }
  }

  __warningMessageWatcher(labelMin, min, searchValue) {
    const searchValueSize = (searchValue || '').length;
    const minCharacterRequired = min - searchValueSize;

    this.$.warningMessage.style.display = searchValueSize < min ? 'block' : 'none';
    this.$.flyoutContent.style.display = searchValueSize < min ? 'none' : 'block';
    this.$.warningMessage.textContent = searchValueSize < min ? labelMin || `Type ${min} letters for results` : '';

    this.$.minCharacterHelp.style.display = searchValue && searchValueSize < min ? 'block' : 'none';

    this.$.minCharacterHelp.textContent = `Type ${minCharacterRequired} more character${
      minCharacterRequired > 1 ? 's' : ''
    }`;
  }

  _onValueNotified(event) {
    super._onValueNotified(event);

    const valueFormatted = this.firstSlotChild.focusedValueFormatted;

    this.$.input.value = this.preserveValue ? valueFormatted : '';

    this.value = valueFormatted;
    this.$.input.focus();
    this.opened = false;
  }

  _flyout__openedChanged(_opened, _openedOld) {
    super._flyout__openedChanged(_opened, _openedOld);

    if (_opened && !_openedOld && this._hasSlotChildren(1)) {
      this.firstSlotChild.clearFocusIndex();
    }

    if (this.firstSlotChild) {
      this.firstSlotChild.setAttribute('aria-expanded', _opened);
    }
  }

  __onKeyUp_enter() {
    if (this.opened && this.validate()) {
      this.firstSlotChild.chooseFocused();
    }
  }

  __onKeyUp_escape() {
    this.$.input.focus();
  }

  __onKeyUp_down(e) {
    if (this.opened) {
      this.firstSlotChild._setFocus__onKeyUp_down(e);
    }
  }

  __onKeyUp_up(e) {
    if (this.opened) {
      this.firstSlotChild._setFocus__onKeyUp_up(e);
    }
  }

  __onFocusIn__input() {
    const valueSize = (this.$.input.value || '').length;
    if (this._hasSlotChildren && valueSize > 0) {
      this.opened = true;
    }
  }

  __valueChanged(value) {
    this.searchValue = value;
    this.opened = false;
  }

  __searchValueChanged(value) {
    const valueSize = (value || '').length;

    this.__toggleCloseButtonDisplay(valueSize == 0);
    this.opened = this._hasSlotChildren && valueSize > 0;

    const allowFiltering = this.mode === 'filter';
    if (allowFiltering && this.opened && this._hasSlotChildren(1)) {
      this.firstSlotChild.filterRule = value;
    }

    if (this.kind !== 'edit') {
      this.value = value;
    }
  }

  __toggleCloseButtonDisplay(hidden) {
    if (this.readonly || this.disabled) {
      hidden = true;
    }
    dom.setAttrs(this.$.inputClear, { hidden: hidden });
  }

  __onClose_triggerEnded() {
    this.$.input.focus();

    this.searchValue = '';
    this.value = '';
  }

  __onInvalid_input(e) {
    // NOTE stops the validation bubble from showing
    e.preventDefault();
    this._dispatchEvent('input-invalid-ended', e);
  }

  /**
   * @return {Boolean}
   */
  checkValidity() {
    return !this.required || !tt.isNil(this.value);
  }

  reportValidity() {
    return this.checkValidity();
  }

  /**
   * @return {ValidityState}
   */
  get validity() {
    const validity = super.validity;

    validity.valid = this.checkValidity();
    validity.valueMissing = !validity.valid;
    return validity;
  }

  /**
   * update the search value immediately or after a searchDelay
   */
  __onInput_input(e) {
    const value = e.target.value;
    const searchDelay = this.searchDelay;
    const isDelayValid = tt.is(Number, searchDelay) && searchDelay > 0;

    if (isDelayValid) {
      this.__onChangeDebouncer = Debouncer.debounce(this.__onChangeDebouncer, timeOut.after(searchDelay), () => {
        this.searchValue = value;
      });
    } else {
      this.searchValue = value;
    }
  }
}

HIP.factory.element(TypeaheadElement);
